/**
 * Framed Page Template
 * 
 * This template renders an iframe that embeds content from kidsland.com.cn.
 * Used for all non-investor-relations pages:
 * - Home page (/)
 * - About Us pages
 * - News pages
 * - Brand pages
 * - Retail pages
 * - Distribution pages
 * 
 * The iframe src is constructed as:
 * https://www.kidsland.com.cn/{url[langKey]}
 * 
 * A loading spinner is shown while the iframe loads.
 */
import React, { useState, useRef } from 'react'
import { PageProps } from 'gatsby'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import FramedLayout from '../components/FramedLayout'
import LoadingSpinner from '../components/LoadingSpinner'
import SEO from '../components/SEO'
import Header from '../components/Header'
import { FramedPageContext, LangKey } from '../types'
import { getLangFieldKey } from '../utils/lang'

// ─── Styled Components ────────────────────────────────────────────────────────

const StyledIframe = styled.iframe<{ scale?: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;

  @media only screen and (max-device-width: 1024px) {
    ${({ scale = 1 }) =>
      scale !== 1
        ? css`
            -ms-zoom: ${scale};
            -moz-transform: scale(${scale});
            -moz-transform-origin: 0 0;
            -o-transform: scale(${scale});
            -o-transform-origin: 0 0;
            -webkit-transform: scale(${scale});
            -webkit-transform-origin: 0 0;
          `
        : ''}
  }
`

// ─── Component ────────────────────────────────────────────────────────────────

interface FramedPageTemplateProps extends PageProps {
  pageContext: FramedPageContext
}

const FramedPageTemplate: React.FC<FramedPageTemplateProps> = ({ pageContext, location }) => {
  const { langKey, name, url, scale = 1 } = pageContext
  const [isLoading, setIsLoading] = useState(true)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const langField = getLangFieldKey(langKey as LangKey)

  // Build iframe src URL
  const urlPath = url[langField].trim()
  const iframeSrc = urlPath.startsWith('/')
    ? `https://www.kidsland.com.cn${urlPath}`
    : `https://www.kidsland.com.cn/${urlPath}`

  const pageTitle = name[langField]

  return (
    <FramedLayout>
      <SEO title={pageTitle} lang={langKey === 'en' ? 'en' : 'zh-TW'} />
      <Header langKey={langKey as LangKey} isFixed={false} currentPath={location.pathname} />

      {/* Loading spinner shown while iframe loads */}
      {isLoading && <LoadingSpinner />}

      {/* Main iframe embedding kidsland.com.cn */}
      <StyledIframe
        ref={iframeRef}
        name="main"
        className="scrollable"
        src={iframeSrc}
        frameBorder={0}
        height="100%"
        width="100%"
        scale={scale}
        onLoad={() => setIsLoading(false)}
        title={pageTitle}
      />
    </FramedLayout>
  )
}

export default FramedPageTemplate
